import { observer } from 'mobx-react-lite';
import React, { memo, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Container,
  Heading,
  majorScale,
  minorScale,
  PageContainer,
  PageLoader,
  Tag,
  VirtualTable,
  VirtualTableHeaderItem,
  VirtualTableItem,
} from '../../npaper';
import DateFilter from '../../npaper/DateFilter';
import { FilterPopover } from '../../npaper/FilterPopover';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { ResponsibleFilter } from '../../npaper/ResponsibleFilter';
import { SearchInput } from '../../npaper/SearchInput';
import { ITask, Task, Unit, useStores } from '../../stores';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { CompanyThumbnail } from '../companies';
import { SaleThumbnail } from '../sales';
import { TaskDialog } from './TaskDialog';
import { TaskStatusTag } from './TaskLogTracking';
import { TaskSideSheet } from './TaskSideSheet';

export interface ITaskTableProps {}

const TasksTable: React.FC<ITaskTableProps> = () => {
  const intl = useIntl();
  const { taskStore } = useStores();

  const [inspectingTask, setInspectingTask] = useState<ITask>();
  const [showInspect, setShowInspect] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  useEffect(() => {
    if (inspectingTask) {
      setShowInspect(true);
    }
  }, [inspectingTask]);

  /** Handle gracefull open of TaskSideSheet, so that it animates close and open of new task */
  const openInspect = (task: ITask) => {
    if (!showInspect) {
      setInspectingTask(task);
      return;
    }
    if (inspectingTask && inspectingTask !== task) {
      setShowInspect(false);
      setTimeout(() => {
        setInspectingTask(task);
      }, 150);
    } else if (inspectingTask === task) {
      setShowInspect(false);
    } else {
      setInspectingTask(task);
    }
  };

  const {
    items,
    isLoading,
    fetchMore,
    total,
    setFilter,
    filter,

    sums,
  } = useFilterFetch<typeof Task>(taskStore as any, {
    ordering: ['-id'].join(','),
  });

  const header: VirtualTableHeaderItem[] = [
    {
      title: intl.formatMessage({ defaultMessage: 'Task title' }),
      width: 260,
      orderByKeys: ['id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Status' }),
      width: 140,
      orderByKeys: ['status', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Customer' }),
      orderByKeys: ['customer__name', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Description' }),
      orderByKeys: ['description', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Time spent' }),
      align: 'right',
      width: 160,
      orderByKeys: ['time_spent', 'id'],
      footer: sums ? (
        <FormattedUnit
          unit={Unit.HOUR}
          value={sums.time_spent__sum / 1000 / 60 / 60} // Remember time_spent is in milliseconds
        />
      ) : undefined,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Budget' }),
      align: 'right',
      width: 160,
      orderByKeys: ['budget', 'id'],
      footer: sums ? (
        <FormattedUnit unit={Unit.HOUR} value={sums.budget__sum} />
      ) : undefined,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Related sale' }),
      align: 'right',
      width: 160,
      orderByKeys: ['sale', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Created' }),
      width: 180,
      orderByKeys: ['created_at'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Due date' }),
      width: 180,
      orderByKeys: ['created_at'],
    },
    {
      title: 'Ansvarlig',
      orderByKeys: [
        'responsible__user__first_name',
        'responsible__user__last_name',
      ],
    },
    { title: 'Tags', orderByKeys: ['tags__name', 'id'] },
  ];

  return !items ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      height="100%"
      maxHeight="100vh"
    >
      {inspectingTask && (
        <TaskSideSheet
          isShown={showInspect}
          onClose={() => {
            setInspectingTask(undefined);
            setShowInspect(false);
          }}
          task={inspectingTask}
        />
      )}
      <TaskDialog
        isShown={isCreatingTask}
        onClose={(task) => {
          setIsCreatingTask(false);
        }}
      />
      <Container marginBottom={majorScale(2)} flexShrink={0} flexGrow={0}>
        <Heading size={700} marginBottom={minorScale(3)}>
          Opgaver
        </Heading>
        <Box display="flex" justifyContent="space-between">
          <SearchInput
            marginRight={majorScale(1)}
            currentQuery={filter.filter ? filter.filter.search : ''}
            onSearch={(query) => {
              setFilter({
                ...filter,
                filter: { ...filter.filter, search: query },
              });
            }}
          />
          <FilterPopover<any>
            filter={filter}
            onChange={(f) => setFilter(f)}
            filterFields={[
              {
                field: 'title',
                label: 'Titel',
                type: 'string',
              },
              {
                field: 'customer',
                label: 'Kunde',
                type: 'company',
              },
              {
                field: 'logged_by',
                label: 'Logget af',
                type: 'user',
              },
            ]}
          />

          <Box display="flex" marginLeft="auto">
            <DateFilter
              dateRange={
                filter.filter?.created_at__gte
                  ? {
                      from: new Date(filter.filter.created_at__gte),
                      to: filter.filter.created_at__lte
                        ? new Date(filter.filter.created_at__lte)
                        : undefined,
                    }
                  : undefined
              }
              onSelect={(range) => {
                let dateFilter: any = {};
                if (range) {
                  dateFilter = {
                    // From
                    created_at__gte: range.from.toISOString(),
                    created_at__lte: range.to
                      ? range.to.toISOString()
                      : undefined,
                  };
                } else {
                  dateFilter = {
                    // From
                    created_at__gte: undefined,
                    created_at__lte: undefined,
                  };
                }
                setFilter((f) => ({
                  ...f,
                  filter: { ...f.filter, ...dateFilter },
                }));
              }}
              marginRight={majorScale(1)}
            />
            <ResponsibleFilter
              onSelect={(members) => {
                setFilter({
                  filter: {
                    ...filter.filter,
                    responsible__in:
                      members.length > 0 ? members.map((m) => m.id) : undefined,
                  },
                  ordering: filter.ordering,
                });
              }}
            />
            <Button
              marginLeft={majorScale(1)}
              iconLeft="add"
              onClick={() => setIsCreatingTask(true)}
            >
              <FormattedMessage defaultMessage="Create task" />
            </Button>
          </Box>
        </Box>
      </Container>
      <VirtualTable
        isLoading={isLoading}
        items={items}
        total={total}
        rowHeight={48}
        ordering={filter.ordering}
        onOrderingChange={(ordering) =>
          setFilter({ filter: filter.filter, ordering })
        }
        onSelect={(selection) => {
          const keys = Object.keys(selection);
          if (keys.length !== 1) {
            return;
          }
          const selectedItem = items[parseInt(keys[0])];
          openInspect(selectedItem);
        }}
        noItemsPlaceholder={
          <Box textAlign="center" paddingY={majorScale(6)}>
            <Heading size={500} marginBottom={majorScale(1)}>
              <FormattedMessage defaultMessage="No matching tasks found" />
            </Heading>
            <p>
              <FormattedMessage
                defaultMessage="Try changing your filters or <link>see all tasks</link>"
                values={{
                  link: (chunk: string) => (
                    <span
                      className="link medium"
                      onClick={() =>
                        setFilter({ filter: {}, ordering: filter.ordering })
                      }
                    >
                      {chunk}
                    </span>
                  ),
                }}
              />
            </p>
          </Box>
        }
        header={header}
        footer={true}
        onRequestMore={async () => fetchMore()}
        renderRow={({ item }) => <MemoizedRow task={item} />}
      />
    </PageContainer>
  );
};

export default TasksTable;

const MemoizedRow = memo(
  observer(({ task }: { task: ITask }) => {
    return (
      <>
        <VirtualTableItem title={task.title}>
          <span>
            <Box is="span" fontWeight={600}>
              ({task.id})
            </Box>{' '}
            {task.title}
          </span>
        </VirtualTableItem>
        <VirtualTableItem>
          <TaskStatusTag task={task} />
        </VirtualTableItem>
        <VirtualTableItem>
          {task.customer ? <CompanyThumbnail company={task.customer} /> : ''}
        </VirtualTableItem>
        <VirtualTableItem title={task.description}>
          <span className="p small">{task.description}</span>
        </VirtualTableItem>
        <VirtualTableItem className="font-mono">
          <Box is="span" marginLeft="auto">
            <FormattedUnit
              unit={Unit.HOUR}
              value={(task.timeSpent || 0) / 1000 / 60 / 60}
            />
          </Box>
        </VirtualTableItem>
        <VirtualTableItem className="font-mono">
          <Box is="span" marginLeft="auto">
            <FormattedUnit unit={Unit.HOUR} value={task.budget} />
          </Box>
        </VirtualTableItem>
        <VirtualTableItem>
          {task.sale ? <SaleThumbnail sale={task.sale} /> : ''}
        </VirtualTableItem>
        <VirtualTableItem>
          {task.created_at ? (
            <FormattedDate format="default" value={task.created_at} />
          ) : (
            ''
          )}
        </VirtualTableItem>
        <VirtualTableItem>
          {task.due_date ? (
            <FormattedDate format="default" value={task.due_date} />
          ) : (
            ''
          )}
        </VirtualTableItem>
        <VirtualTableItem>
          {task.responsible.length > 0 &&
            (task.responsible.reduce((p, n) => !!p && !!n, true)
              ? task.responsible.map((member) => member!.user.name).join(', ')
              : '')}
        </VirtualTableItem>
        <VirtualTableItem>
          {task.tags.map((t) => (
            <Tag key={t} small={true} marginRight={minorScale(1)} intent="info">
              {t}
            </Tag>
          ))}
        </VirtualTableItem>
      </>
    );
  })
);
